// skeleton\app\src\components\ProviderProfile\CommentsSidebar.tsx

import React, { useCallback, useEffect, useRef, useState } from "react";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Sidebar } from "primereact/sidebar";
import { useAuthContext } from "../../Context/AuthContext";
import { ApiEndpoints } from "../../constants/appConstants";
import httpService from "../../services/httpService";
import { Comment } from "./models";
import { formatToLocalDateTime } from "../../services/dateTimeService";

interface CommentsSidebarProps {
  sidebarVisible: boolean;
  handleCommentsSidebarHide: () => void;
  commentMeasureNumber: string;
  commentProviderMPI: string;
  commentLevel: string;
  commentMeasureId: number;
  updatePerformanceAreasForComments: (measureId: number) => void;
  externalSave?: () => void;
  providerAgencyName: string;
  alertMessage?: string;
  providerType?: string;
}

export default function CommentsSidebar({
  sidebarVisible,
  handleCommentsSidebarHide,
  commentMeasureNumber,
  commentProviderMPI,
  commentLevel,
  commentMeasureId,
  updatePerformanceAreasForComments,
  providerAgencyName,
  externalSave,
  alertMessage,
  providerType,
}: CommentsSidebarProps) {
  const [currentComment, setCurrentComment] = useState("");
  const [commentData, setCommentData] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);

  const { appUser } = useAuthContext();
  const lastCommentRef = useRef<HTMLDivElement | null>(null);

  const INVALID_COMMENT_CHARS = /[<>#{}*']/;
  const trimmedComment = currentComment.trim();
  const hasInvalidChars = INVALID_COMMENT_CHARS.test(currentComment);
  const isSaveDisabled =
    loading || trimmedComment.length === 0 || hasInvalidChars;

  const getAllComments = useCallback(async () => {
    try {
      setLoading(true);

      const response: any = await httpService.post(
        ApiEndpoints.GetAllComments,
        {
          commentLevel,
          providerMPI: commentProviderMPI,
          measureId: commentMeasureId,
          providerType,
        }
      );

      if (response.status === 200) {
        const comments = Array.isArray(response?.data)
          ? [...response.data]
          : [];

        comments.sort(
          (a: Comment, b: Comment) =>
            new Date(a.createdDate).getTime() -
            new Date(b.createdDate).getTime()
        );

        setCommentData(comments);
      }
    } catch (error: any) {
      console.error("[CommentsSidebar] Error retrieving comments.", {
        commentLevel,
        providerMPI: commentProviderMPI,
        measureId: commentMeasureId,
        providerType,
        message: error?.message,
        responseStatus: error?.response?.status,
        responseData: error?.response?.data,
      });
    } finally {
      setLoading(false);
    }
  }, [
    commentLevel,
    commentProviderMPI,
    commentMeasureId,
    providerType,
  ]);

  const saveComment = async () => {
    if (trimmedComment.length === 0 || hasInvalidChars || loading) {
      return;
    }

    try {
      setLoading(true);

      const response: any = await httpService.post(
        ApiEndpoints.SaveComment,
        {
          commentAddedBy: appUser?.name,
          commentDescription: trimmedComment,
          commentLevel,
          providerMPI: commentProviderMPI,
          measureId: commentMeasureId,
          providerType,
        }
      );

      if (response.status === 200) {
        setCurrentComment("");

        await getAllComments();

        if (commentLevel === "measure") {
          updatePerformanceAreasForComments(commentMeasureId);
        }

        externalSave?.();
      }
    } catch (error: any) {
      console.error("[CommentsSidebar] Error saving comment.", {
        commentLevel,
        providerMPI: commentProviderMPI,
        measureId: commentMeasureId,
        providerType,
        message: error?.message,
        responseStatus: error?.response?.status,
        responseData: error?.response?.data,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!sidebarVisible) {
      return;
    }

    void getAllComments();
  }, [sidebarVisible, getAllComments]);

  useEffect(() => {
    if (lastCommentRef.current) {
      lastCommentRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [commentData]);

  const handleHide = () => {
    setCurrentComment("");
    handleCommentsSidebarHide();
  };

  let commentSection: React.JSX.Element[] | React.JSX.Element =
    commentData.map((comment, index) => {
      const formattedDateTime = formatToLocalDateTime(
        comment.createdDate
      );

      return (
        <div
          className="comment-container"
          ref={
            index === commentData.length - 1
              ? lastCommentRef
              : null
          }
          key={comment.commentId ?? index}
        >
          <div className="comment-header">
            <div className="comment-username">
              {comment.commentAddedBy}
            </div>

            <div className="comment-date">
              {formattedDateTime}
            </div>
          </div>

          <div className="comment">
            {comment.commentDescription}
          </div>
        </div>
      );
    });

  if (alertMessage) {
    commentSection = (
      <>
        <div
          className="alert-message"
          style={{ color: "red" }}
          role="alert"
        >
          {alertMessage}
        </div>

        {commentSection}
      </>
    );
  } else if (!commentData.length) {
    commentSection = loading ? (
      <span className="comments-loader">Loading...</span>
    ) : (
      <span className="no-comments-found">
        No Comments Found
      </span>
    );
  }

  return (
    <div className="right-sidebar">
      <Sidebar
        visible={sidebarVisible}
        onHide={handleHide}
        position="right"
        style={{ width: "28rem" }}
        showCloseIcon={false}
      >
        <div className="comments-sidebar-header">
          <h3>
            Comments for {commentMeasureNumber}{" "}
            {commentLevel === "measureDetails"
              ? "Data Dictionary"
              : `(${providerAgencyName})`}
          </h3>

          <Button
            onClick={handleHide}
            icon="pi pi-times"
            size="small"
            rounded
            outlined
            severity="secondary"
            aria-label="Close comments"
            className="comments-close-button"
          />
        </div>

        <div className="comments-sidebar-content">
          <div className="comments">{commentSection}</div>

          <div className="comments-form">
            <label
              htmlFor="commentsInput"
              className="sr-only"
            >
              Enter comment
            </label>

            <InputTextarea
              id="commentsInput"
              value={currentComment}
              rows={3}
              placeholder="Enter your comment here"
              onChange={(event) =>
                setCurrentComment(event.target.value)
              }
              aria-invalid={hasInvalidChars}
              aria-describedby={
                hasInvalidChars
                  ? "commentsInputError"
                  : undefined
              }
            />

            {hasInvalidChars && (
              <div
                id="commentsInputError"
                className="alert-message"
                style={{
                  color: "red",
                  marginTop: "6px",
                }}
                role="alert"
              >
                Invalid characters not allowed: &lt; &gt;{" "}
                {"{"} {"}"} # * '
              </div>
            )}

            <Button
              className="common-button"
              id="saveComment"
              disabled={isSaveDisabled}
              onClick={() => {
                void saveComment();
              }}
              loading={loading}
            >
              Save Comment
            </Button>
          </div>
        </div>
      </Sidebar>
    </div>
  );
}
